import { Injectable, inject } from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { BehaviorSubject } from 'rxjs'

export const EDITOR_MAXIMIZED_QUERY_PARAM = 'editor.max'
export const EDITOR_ZOOM_QUERY_PARAM = 'editor.zoom'

export const EDITOR_ZOOM_MIN = 0.5
export const EDITOR_ZOOM_MAX = 2
export const EDITOR_ZOOM_STEP = 0.1
export const EDITOR_ZOOM_DEFAULT = 1

function clampZoom(value: number): number {
  const clamped = Math.min(EDITOR_ZOOM_MAX, Math.max(EDITOR_ZOOM_MIN, value))
  return Math.round(clamped * 100) / 100
}

@Injectable({
  providedIn: 'root',
})
export class EditorSettingsService {
  private router = inject(Router)
  private route = inject(ActivatedRoute)

  private showCitationsSubject = new BehaviorSubject<boolean>(true)
  showCitations$ = this.showCitationsSubject.asObservable()

  get showCitations(): boolean {
    return this.showCitationsSubject.value
  }

  setShowCitations(value: boolean) {
    this.showCitationsSubject.next(value)
  }

  private showWordCountSubject = new BehaviorSubject<boolean>(false)
  showWordCount$ = this.showWordCountSubject.asObservable()

  get showWordCount(): boolean {
    return this.showWordCountSubject.value
  }

  setShowWordCount(value: boolean) {
    this.showWordCountSubject.next(value)
  }

  private zoomLevelSubject = new BehaviorSubject<number>(EDITOR_ZOOM_DEFAULT)
  zoomLevel$ = this.zoomLevelSubject.asObservable()

  get zoomLevel(): number {
    return this.zoomLevelSubject.value
  }

  setZoomLevel(value: number) {
    const next = clampZoom(value)
    if (this.zoomLevelSubject.value === next) {
      return
    }
    this.zoomLevelSubject.next(next)
    this.syncQueryParam({ [EDITOR_ZOOM_QUERY_PARAM]: next === EDITOR_ZOOM_DEFAULT ? null : next }, () =>
      this.applyZoomFromParams(this.route.snapshot.queryParams)
    )
  }

  zoomIn() {
    this.setZoomLevel(this.zoomLevel + EDITOR_ZOOM_STEP)
  }

  zoomOut() {
    this.setZoomLevel(this.zoomLevel - EDITOR_ZOOM_STEP)
  }

  resetZoom() {
    this.setZoomLevel(EDITOR_ZOOM_DEFAULT)
  }

  private isEditorMaximizedSubject = new BehaviorSubject<boolean>(false)
  isEditorMaximized$ = this.isEditorMaximizedSubject.asObservable()

  constructor() {
    this.route.queryParams.subscribe((params) => {
      this.applyMaximizedFromParams(params)
      this.applyZoomFromParams(params)
    })
  }

  get isEditorMaximized(): boolean {
    return this.isEditorMaximizedSubject.value
  }

  setIsEditorMaximized(value: boolean) {
    if (this.isEditorMaximizedSubject.value === value) {
      return
    }
    this.isEditorMaximizedSubject.next(value)
    this.syncQueryParam({ [EDITOR_MAXIMIZED_QUERY_PARAM]: value ? 't' : null }, () => this.applyMaximizedFromParams(this.route.snapshot.queryParams))
  }

  private syncQueryParam(queryParams: Params, rollback: () => void) {
    this.router
      .navigate([], {
        relativeTo: this.getDeepestActivatedRoute(),
        queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .then((success) => {
        if (!success) {
          rollback()
        }
      })
      .catch(rollback)
  }

  updateMaximizedStateOnly(value: boolean) {
    if (this.isEditorMaximizedSubject.value === value) {
      return
    }
    this.isEditorMaximizedSubject.next(value)
  }

  private applyMaximizedFromParams(params: Params) {
    const value = params[EDITOR_MAXIMIZED_QUERY_PARAM] === 't'
    if (this.isEditorMaximizedSubject.value !== value) {
      this.isEditorMaximizedSubject.next(value)
    }
  }

  private applyZoomFromParams(params: Params) {
    const raw = params[EDITOR_ZOOM_QUERY_PARAM]
    const parsed = raw != null ? Number(raw) : EDITOR_ZOOM_DEFAULT
    const value = Number.isFinite(parsed) ? clampZoom(parsed) : EDITOR_ZOOM_DEFAULT
    if (this.zoomLevelSubject.value !== value) {
      this.zoomLevelSubject.next(value)
    }
  }

  private getDeepestActivatedRoute(): ActivatedRoute {
    const findLeaf = (route: ActivatedRoute): ActivatedRoute => (route.firstChild === null ? route : findLeaf(route.firstChild))
    return findLeaf(this.router.routerState.root)
  }
}
